import { InboxOutlined } from '@ant-design/icons'
import Dragger from 'antd/lib/upload/Dragger';
import { DraggerProps } from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react'
import { RcFile, UploadFile } from 'antd/lib/upload/interface';
import { RootStoreContext } from '../../stores/rootStore';
import { toast } from 'react-toastify';
import { IMediaItem } from '../../models/MediaItem';
import { FileType } from '../../models/FileType';
import { Modal, Select } from 'antd';

interface IProps {
	uploadUrl: string;
	uploadedFiles?: IMediaItem[];
	setUploadedFiles: (files: IMediaItem[]) => void;
	collectionId: string;
}

interface UploadedFile {
	uid: string;
	name: string;
	id: string;
	link: string;
	type?: FileType;
}

const DragUploader: React.FC<IProps> = (props) => {
	const rootContext = useContext(RootStoreContext);
	const {uploadUrl, uploadedFiles = [], setUploadedFiles, collectionId} = props

	const [files, setFiles] = useState<UploadedFile[]>(uploadedFiles?.map(x => {
		return {
			uid: x.publicId,
			name: x.title,
			id: x.id,
			link: x.link,
			type: x.type
		}
	}) ?? []);
	
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedFileType, setSelectedFileType] = useState(FileType.Default);
	const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
	
	const handleModalOk = async () => {
		if (!pendingFile) return;

		setModalVisible(false);

		// Create FormData for upload
		const formData = new FormData();
		formData.append('collectionId', props.collectionId);
		formData.append('file', pendingFile);
		formData.append('type', selectedFileType.toString());
		
		try {
			// Upload the file with the selected type
			const response = await fetch(uploadUrl, {
				method: 'POST',
				body: formData,
				headers: {
					Authorization: `Bearer ${token}`
				}
			});
			
			if (response.ok) {
				const result = await response.json();
				// Add the uploaded file to the files state
				setFiles([...files, {
					id: result.id,
					uid: result.publicId,
					name: result.title,
					link: result.link,
					type: selectedFileType
				}]);
				toast.success('File uploaded successfully!');
			} else {
				toast.error('Error uploading file!');
			}
		} catch (error) {
			toast.error('Error uploading file!');
		}
		
		// Reset pending file
		setPendingFile(null);
	};
	
	const handleModalCancel = () => {
		setModalVisible(false);
		setPendingFile(null);
	};

	const onRemove = async (file: UploadFile<any>): Promise<void> => {
		await rootContext.mediaItemStore.deleteMediaItem(collectionId, file.uid);
		setFiles([...files.filter(x => x.uid !== file.uid)])
	}

	const token = window.localStorage.getItem('jwt');

	const uploadProps: DraggerProps = {
		name: 'file',
		multiple: true,
		action: uploadUrl,
		headers: {Authorization: `Bearer ${token}`},
		onRemove: onRemove,
		defaultFileList: [...files.map(x => ({
			uid: x.uid,
			name: x.name,
			status: 'done' as const,
			url: x.link
		}))],
		beforeUpload: (file) => {
			// Prevent auto-upload and show modal instead
			setPendingFile(file);
			setSelectedFileType(FileType.Default);
			setModalVisible(true);
			return false;
		},
		onDrop(e: any) {
			console.log('Dropped files', e.dataTransfer.files);
		},
	};

	useEffect(() => {
		setUploadedFiles(files.map(x => {
			return {
				publicId: x.uid,
				title: x.name,
				id: x.id,
				link: x.link,
				type: x.type
			}
		}))
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [files])

	return (
		<>
			<Dragger {...uploadProps}>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">Click or drag file to this area to upload</p>
				<p className="ant-upload-hint">
					Support for a single or bulk upload. Strictly prohibited from uploading large, video or media files.
				</p>
			</Dragger>
			
			<Modal
				title="Select File Type"
				visible={modalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
			>
				<Select
					style={{ width: '100%' }}
					value={selectedFileType}
					onChange={(value) => setSelectedFileType(value as FileType)}
				>
					<Select.Option value={FileType.Default}>Default</Select.Option>
					<Select.Option value={FileType.Pod}>POD</Select.Option>
				</Select>
			</Modal>
		</>
	)
}

export default DragUploader
